import React, { useEffect, useState, useRef } from "react";
import { notification } from "antd";
import { FileImageOutlined, TableOutlined, DatabaseOutlined } from "@ant-design/icons";
import { pie, arc, PieArcDatum } from "d3-shape";
import { select } from "d3-selection";
import { ConditionalTooltip } from "../ConditionalTooltip";

export type PieChartData = {
    title: string;
    isDonutChart: boolean;
    slices: Array<{
        label: string;
        value: number;
        color: string;
    }>;
};

export const isPieChartData = (columns: any[], dataSource: any[]): boolean => {
    if (!columns || !dataSource || dataSource.length === 0) {
        return false;
    }

    const columnTitles = columns.map(col => (col.title || "").trim());

    // Check for regular pie chart (4 columns)
    if (columns.length === 4) {
        const requiredColumns = ["pie_category", "pie_value", "pie_color", "pie_title"];
        return requiredColumns.every(reqCol => columnTitles.includes(reqCol));
    }

    // Check for donut chart (5 columns)
    if (columns.length === 5) {
        const requiredColumns = ["pie_category", "pie_value", "pie_color", "pie_type", "pie_title"];
        if (!requiredColumns.every(reqCol => columnTitles.includes(reqCol))) {
            return false;
        }
        // Verify pie_type is "donut"
        const typeCol = columns.find(col => (col.title || "").trim() === "pie_type");
        if (typeCol && dataSource.length > 0) {
            const typeValue = String(dataSource[0]?.[typeCol.dataIndex] || "").trim().toLowerCase();
            return typeValue === "donut";
        }
        return false;
    }

    return false;
};

export const extractPieChartData = (columns: any[], dataSource: any[]): PieChartData | null => {
    if (!isPieChartData(columns, dataSource)) {
        return null;
    }

    const categoryCol = columns.find(col => (col.title || "").trim() === "pie_category");
    const valueCol = columns.find(col => (col.title || "").trim() === "pie_value");
    const colorCol = columns.find(col => (col.title || "").trim() === "pie_color");
    const titleCol = columns.find(col => (col.title || "").trim() === "pie_title");
    const typeCol = columns.find(col => (col.title || "").trim() === "pie_type");

    if (!categoryCol || !valueCol || !colorCol || !titleCol) {
        return null;
    }

    const title = String(dataSource[0]?.[titleCol.dataIndex] || "").trim();

    // Check if this is a donut chart
    const isDonutChart = typeCol ? String(dataSource[0]?.[typeCol.dataIndex] || "").trim().toLowerCase() === "donut" : false;

    const slices = dataSource.map(row => {
        const valueStr = String(row[valueCol.dataIndex] || "0").replace(/,/g, "");
        return {
            label: String(row[categoryCol.dataIndex] || "").trim(),
            value: parseFloat(valueStr) || 0,
            color: String(row[colorCol.dataIndex] || "#000000").trim()
        };
    });

    return { title, isDonutChart, slices };
};

export function PieChart(props: {
    data: PieChartData;
    darkMode: boolean;
    jobAlias?: string;
    onDownloadCsv?: () => void;
    onDownloadPng?: () => void;
    onAddQuery?: () => void;
}) {
    const { data, darkMode, onDownloadCsv, onDownloadPng, onAddQuery } = props;
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 400, height: 400 });
    const [pressedCsv, setPressedCsv] = useState(false);
    const [pressedPng, setPressedPng] = useState(false);
    const [isAddQueryHovered, setIsAddQueryHovered] = useState(false);
    const [pressedAddQuery, setPressedAddQuery] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return;

        let currentWidth = 0;
        let currentHeight = 0;
        let isInitialized = false;

        const updateDimensions = (width: number) => {
            if (width > 0) {
                const chartWidth = Math.min(width - 40, 600);
                const chartHeight = chartWidth; // Square aspect ratio

                // Only update if dimensions actually changed
                if (chartWidth !== currentWidth || chartHeight !== currentHeight) {
                    currentWidth = chartWidth;
                    currentHeight = chartHeight;
                    setDimensions({ width: chartWidth, height: chartHeight });
                }
            }
        };

        // Initial dimensions - wait for next frame to ensure layout is complete
        requestAnimationFrame(() => {
            if (!containerRef.current) return;
            const initialWidth = containerRef.current.clientWidth || containerRef.current.offsetWidth;
            if (initialWidth > 0) {
                updateDimensions(initialWidth);
                isInitialized = true;
            }
        });

        // Observe container for size changes with a small debounce
        let resizeTimeout: NodeJS.Timeout;
        const resizeObserver = new ResizeObserver(entries => {
            if (!isInitialized) return; // Ignore resize events until initial dimensions are set
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                for (const entry of entries) {
                    const { width } = entry.contentRect;
                    if (width > 0) {
                        updateDimensions(width);
                    }
                }
            }, 10);
        });

        // Delay observer setup slightly to avoid immediate fire
        const setupTimeout = setTimeout(() => {
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
        }, 50);

        return () => {
            clearTimeout(resizeTimeout);
            clearTimeout(setupTimeout);
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!svgRef.current || data.slices.length === 0) return;

        const svg = select(svgRef.current);
        svg.selectAll("*").remove();

        const width = dimensions.width;
        const height = dimensions.height;
        const radius = Math.min(width, height) / 2 - 10; // Further increased radius for bigger pie
        const centerX = width / 2;
        const centerY = height / 2;

        type SliceData = typeof data.slices[0];
        const pieGenerator = pie<SliceData>()
            .value((d: SliceData) => d.value)
            .sort(null);

        const innerRadius = data.isDonutChart ? radius * 0.4 : 0;
        const arcGenerator = arc<PieArcDatum<SliceData>>()
            .innerRadius(innerRadius)
            .outerRadius(radius);

        const labelArcGenerator = arc<PieArcDatum<SliceData>>()
            .innerRadius(radius * 0.6)
            .outerRadius(radius * 0.6);

        const g = svg.append("g")
            .attr("transform", `translate(${centerX},${centerY})`);

        const arcs = g.selectAll(".arc")
            .data(pieGenerator(data.slices))
            .enter()
            .append("g")
            .attr("class", "arc");

        arcs.append("path")
            .attr("d", arcGenerator)
            .attr("fill", (d: PieArcDatum<SliceData>) => d.data.color)
            .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
            .attr("stroke-width", 2)
            .on("mouseover", function(this: SVGPathElement, event: MouseEvent, d: PieArcDatum<SliceData>) {
                select(this)
                    .attr("opacity", 0.8)
                    .attr("stroke-width", 3);
            })
            .on("mouseout", function(this: SVGPathElement, event: MouseEvent, d: PieArcDatum<SliceData>) {
                select(this)
                    .attr("opacity", 1)
                    .attr("stroke-width", 2);
            });

        arcs.append("text")
            .attr("transform", (d: PieArcDatum<SliceData>) => {
                const [x, y] = labelArcGenerator.centroid(d);
                return `translate(${x},${y})`;
            })
            .attr("text-anchor", "middle")
            .attr("fill", darkMode ? "#ffffff" : "#000000")
            .attr("font-size", "12px")
            .attr("font-weight", "500")
            .text((d: PieArcDatum<SliceData>) => {
                const percentage = ((d.endAngle - d.startAngle) / (2 * Math.PI) * 100).toFixed(1);
                return `${percentage}%`;
            });

        // Calculate legend width needed
        const maxLegendTextWidth = Math.max(...data.slices.map(slice => {
            const text = `${slice.label} (${slice.value.toLocaleString()})`;
            return text.length * 7 + 40; // Approximate width: ~7px per character + 40px for color box and padding
        }));

        const legendX = centerX + radius + 60;
        const totalWidth = Math.max(width, legendX + maxLegendTextWidth + 20);

        // Update SVG dimensions to fit legend
        svg.attr("width", totalWidth);
        svg.attr("viewBox", `0 0 ${totalWidth} ${height}`);

        const legend = svg.append("g")
            .attr("class", "legend")
            .attr("transform", `translate(${legendX}, 20)`);

        const legendItems = legend.selectAll(".legend-item")
            .data(data.slices)
            .enter()
            .append("g")
            .attr("class", "legend-item")
            .attr("transform", (_d: SliceData, i: number) => `translate(0, ${i * 25})`);

        legendItems.append("rect")
            .attr("width", 15)
            .attr("height", 15)
            .attr("fill", (d: SliceData) => d.color);

        legendItems.append("text")
            .attr("x", 20)
            .attr("y", 12)
            .attr("fill", darkMode ? "#ffffff" : "#000000")
            .attr("font-size", "12px")
            .text((d: SliceData) => `${d.label} (${d.value.toLocaleString()})`);

    }, [data, dimensions, darkMode]);

    const handleDownloadCsv = () => {
        setPressedCsv(true);
        setTimeout(() => setPressedCsv(false), 200);
        if (onDownloadCsv) {
            onDownloadCsv();
        }
    };

    const handleDownloadPng = async () => {
        if (!svgRef.current) return;

        setPressedPng(true);
        setTimeout(() => setPressedPng(false), 200);

        if (onDownloadPng) {
            onDownloadPng();
        }

        try {
            const svg = svgRef.current.cloneNode(true) as SVGElement;

            // Calculate the actual width needed (chart + legend + padding)
            const chartWidth = dimensions.width;
            const chartHeight = dimensions.height;
            const radius = Math.min(chartWidth, chartHeight) / 2 - 10;
            const centerX = chartWidth / 2;
            const legendX = centerX + radius + 60;

            // Calculate max legend width needed
            const maxLegendTextWidth = Math.max(...data.slices.map(slice => {
                const text = `${slice.label} (${slice.value.toLocaleString()})`;
                // Approximate text width: ~7px per character for 12px font
                return text.length * 7 + 40; // 40px for color box and padding
            }));

            // Total width needed: chart center + radius + legend space + padding
            const totalWidth = Math.max(chartWidth, legendX + maxLegendTextWidth + 20);
            const totalHeight = chartHeight;

            // Set SVG dimensions to include full legend
            svg.setAttribute('width', totalWidth.toString());
            svg.setAttribute('height', totalHeight.toString());
            svg.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);

            const svgData = new XMLSerializer().serializeToString(svg);

            // Create a data URL with proper namespace
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            // Scale factor for higher resolution (2x for retina quality)
            const scale = 2;

            img.onload = () => {
                // Set canvas size with scale factor for higher resolution
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                if (ctx) {
                    // Scale the context for high DPI
                    ctx.scale(scale, scale);

                    // Fill background
                    ctx.fillStyle = darkMode ? '#2d2d2d' : '#ffffff';
                    ctx.fillRect(0, 0, img.width, img.height);

                    // Draw the SVG image
                    ctx.drawImage(img, 0, 0);

                    // Convert to blob and download
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.setAttribute('href', blobUrl);
                            const filename = `${data.title.replace(/[<>:"/\\|?*]/g, '')}.png`;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(blobUrl);
                        }
                    }, 'image/png', 1.0); // Maximum quality
                }
                URL.revokeObjectURL(url);
            };

            img.onerror = () => {
                console.error('Error loading SVG image');
                URL.revokeObjectURL(url);
                notification.error({
                    message: 'Failed to download PNG',
                    description: 'An error occurred while generating the PNG image.',
                    placement: 'bottomLeft',
                    duration: 3
                });
            };

            img.src = url;
        } catch (error) {
            console.error('Error downloading PNG:', error);
            notification.error({
                message: 'Failed to download PNG',
                description: 'An error occurred while generating the PNG image.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    return (
        <div ref={containerRef} style={{ marginBottom: "1em", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: "100%" }}>
            <h3 style={{
                color: darkMode ? "#ffffff" : "#000000",
                marginBottom: "1em",
                textAlign: "center"
            }}>
                {data.title}
            </h3>
            <div style={{ position: "relative", display: "inline-block" }}>
            <svg
                ref={svgRef}
                width={dimensions.width}
                height={dimensions.height}
                style={{ maxWidth: "100%", overflow: "visible" }}
                viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            />
                <div style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    display: "flex",
                    gap: "0.75em"
                }}>
                    <ConditionalTooltip title="Download pie chart as PNG">
                        <button
                            onClick={handleDownloadPng}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedPng ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <FileImageOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Download pie chart data as CSV">
                        <button
                            onClick={handleDownloadCsv}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedCsv ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <TableOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Add query">
                        <button
                            onClick={() => {
                                setPressedAddQuery(true);
                                setTimeout(() => setPressedAddQuery(false), 200);
                                onAddQuery?.();
                            }}
                            onMouseEnter={() => setIsAddQueryHovered(true)}
                            onMouseLeave={() => setIsAddQueryHovered(false)}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: isAddQueryHovered
                                    ? (darkMode ? "#ffffff" : "#000000")
                                    : (darkMode ? "#888888" : "#AFAFAF"),
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedAddQuery ? "scale(0.85)" : "scale(1)"
                            }}
                        >
                            <DatabaseOutlined />
                        </button>
                    </ConditionalTooltip>
                </div>
            </div>
        </div>
    );
};

